import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { MetricExplanationButton } from './MetricExplanationButton';

interface MetricCardProps {
  label: string;
  value: string | number;
  subValue?: string;
  tooltip?: string;
  trend?: 'up' | 'down' | 'neutral';
  performanceRank?: 1 | 2 | 3; // 1 = best, 2 = middle, 3 = worst
  className?: string;
}

export function MetricCard({ label, value, subValue, tooltip, trend, performanceRank, className }: MetricCardProps) {
  const getTrendColor = () => {
    // Use performance rank coloring if provided
    if (performanceRank === 1) return 'text-status-good';
    if (performanceRank === 2) return 'text-status-warning';
    if (performanceRank === 3) return 'text-status-critical';
    // Fall back to trend-based coloring
    if (trend === 'up') return 'value-positive';
    if (trend === 'down') return 'value-negative';
    return 'text-foreground';
  };

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs uppercase tracking-wider">
        <span>{label}</span>
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle size={12} className="cursor-help opacity-60 hover:opacity-100" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-sm">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <MetricExplanationButton metricName={label} value={value} className="h-3 w-3 opacity-60 hover:opacity-100" />
        )}
      </div>
      <div className={cn('font-mono text-xl font-semibold', getTrendColor())}>
        {value}
      </div>
      {subValue && (
        <div className="text-xs text-muted-foreground">{subValue}</div>
      )}
    </div>
  );
}
